import React, { useEffect, useState, useRef } from 'react';
import { XIcon, SendIcon, BotIcon, UserIcon, ArrowLeftIcon } from 'lucide-react';
interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}
interface AIChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function AIChatModal({
  isOpen,
  onClose
}: AIChatModalProps) {
  const [messages, setMessages] = useState<Message[]>([{
    role: 'assistant',
    content: "Hello! I'm your AI Pharmacist Assistant for Saudi Arabia. I can help with medication info, health questions, and wellness advice. How can I help you today?",
    timestamp: new Date()
  }]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll on mobile when full-screen
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen]);
  // Auto-grow textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
    }
  }, [inputMessage]);
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    const userMessage: Message = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('https://requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [{
            role: 'system',
            content: `You are an AI Pharmacist Assistant for Innova Pharmacy in Saudi Arabia.
RESPONSE FORMAT:
- Use clear sections with headers when appropriate
- Use bullet points (•) for lists
- Use line breaks for readability
- Bold important information using **text**
- ALWAYS include product links using this format: [Product Name](URL)
- Keep responses concise but informative (3-5 sentences or organized bullets)

YOUR ROLE:
• Answer medication and health questions
• Recommend products from Innova's catalog WITH LINKS
• Provide dosage and usage guidance
• Offer health and wellness advice

GUIDELINES:
✓ Always mention prices in SAR
✓ For serious symptoms → recommend seeing a doctor immediately
✓ Remind users you're an AI assistant, not a replacement for professional medical advice
✓ Serve Saudi customers with culturally sensitive, practical advice`
          }, ...messages.map(msg => ({
            role: msg.role,
            content: msg.content
          })), {
            role: 'user',
            content: inputMessage
          }],
          temperature: 0.7,
          max_tokens: 400
        })
      });
      if (!response.ok) {
        throw new Error('Failed to get response from AI');
      }
      const data = await response.json();
      const assistantMessage: Message = {
        role: 'assistant',
        content: data.choices[0].message.content,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      setError('Sorry, I encountered an error. Please try again or visit your nearest Innova Pharmacy branch.');
      console.error('AI Chat Error:', err);
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  if (!isOpen) return null;
  const suggestedPrompts = ['Best vitamin for tiredness?', 'Headache remedies', 'Vitamin D dosage'];
  return <>
      {/* Desktop overlay */}
      <div className="hidden md:block fixed inset-0 bg-black bg-opacity-50 z-[200]" onClick={onClose} data-id="element-3697" data-mp="2jrxjf44753qc" />
      {/* Container — full-screen on mobile, floating card on desktop */}
      <div className="
          fixed z-[201] bg-white flex flex-col
          inset-0
          md:inset-auto md:bottom-4 md:left-4 md:lg:left-6
          md:w-[400px] md:max-w-[calc(100vw-2rem)]
          md:max-h-[640px] md:rounded-2xl md:shadow-2xl
        " data-id="element-3698" data-mp="2slakwugbtdo1">
        {/* Header — sticky */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 px-3 md:px-4 py-3 md:rounded-t-2xl flex items-center justify-between flex-shrink-0 pt-[max(0.75rem,env(safe-area-inset-top))]" data-id="element-3699" data-mp="1w0a7bsblfx84">
          <div className="flex items-center gap-2 md:gap-3 min-w-0" data-id="element-3700" data-mp="1qqpn092k7lm6">
            <button onClick={onClose} className="md:hidden text-white p-1.5 -ml-1.5 rounded-full active:bg-white/20" aria-label="Back" data-id="element-3701" data-mp="1c1joyofgk1fv">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-3702" data-mp="24pz1n4b4a7o1" />
            </button>
            <div className="w-9 h-9 md:w-10 md:h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0" data-id="element-3703" data-mp="29oa958dxw633">
              <BotIcon className="w-5 h-5 md:w-6 md:h-6 text-green-600" data-id="element-3704" data-mp="1upomhe5iuq68" />
            </div>
            <div className="min-w-0" data-id="element-3705" data-mp="1zojhrfkoe778">
              <h3 className="text-white font-bold text-sm md:text-base truncate" data-id="element-3706" data-mp="1zrnqmq935r48">
                AI Pharmacist
              </h3>
              <p className="text-white/90 text-[11px] md:text-xs flex items-center gap-1" data-id="element-3707" data-mp="oa5zcq8oyjyl">
                <span className="w-1.5 h-1.5 rounded-full bg-green-200 animate-pulse" data-id="element-3708" data-mp="2qwte4rgi05l0" />
                Online · Powered by AI
              </p>
            </div>
          </div>
          <button onClick={onClose} className="hidden md:block text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors" aria-label="Close" data-id="element-3709" data-mp="2m02xa1wvd5yj">
            <XIcon className="w-5 h-5" data-id="element-3710" data-mp="2793td46zb5la" />
          </button>
        </div>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 md:px-4 py-3 md:py-4 space-y-3 md:space-y-4 bg-gray-50 overscroll-contain" data-id="element-3711" data-mp="276r5kaeji10b">
          {messages.map((message, index) => <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-3712" data-mp="1y8ns81dpkajl" data-mpi={index}>
              <div className="flex items-start gap-2 max-w-[85%]" data-id="element-3713" data-mp="3smoqak1fotim">
                {message.role === 'assistant' && <div className="w-7 h-7 md:w-8 md:h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-3714" data-mp="3non8s3jnp299">
                    <BotIcon className="w-4 h-4 md:w-5 md:h-5 text-green-600" data-id="element-3715" data-mp="3v50jg68hq88t" />
                  </div>}
                <div className={`rounded-2xl px-3.5 py-2.5 md:px-4 md:py-3 ${message.role === 'user' ? 'bg-[#1E2B55] text-white rounded-br-md' : 'bg-white text-gray-800 border border-gray-200 shadow-sm rounded-bl-md'}`} data-id="element-3716" data-mp="8rjuse53qpgo">
                  <div className="text-sm leading-relaxed" style={{
                wordBreak: 'break-word',
                direction: /[\u0600-\u06FF]/.test(message.content) ? 'rtl' : 'ltr'
              }} dangerouslySetInnerHTML={{
                __html: formatMessage(message.content)
              }} data-id="element-3717" data-mp="3c9jyhh3mk8oh" />
                </div>
                {message.role === 'user' && <div className="w-7 h-7 md:w-8 md:h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-3718" data-mp="1rj2vg7lf7uzm">
                    <UserIcon className="w-4 h-4 md:w-5 md:h-5 text-blue-600" data-id="element-3719" data-mp="1ml19newm1hkg" />
                  </div>}
              </div>
            </div>)}
          {isLoading && <div className="flex justify-start" data-id="element-3720" data-mp="1wh4onlck9aot">
              <div className="flex items-start gap-2 max-w-[85%]" data-id="element-3721" data-mp="1wgcsa8b538xh">
                <div className="w-7 h-7 md:w-8 md:h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-3722" data-mp="2a7zo67166ffq">
                  <BotIcon className="w-4 h-4 md:w-5 md:h-5 text-green-600" data-id="element-3723" data-mp="26abnsogpjugg" />
                </div>
                <div className="bg-white text-gray-800 border border-gray-200 shadow-sm rounded-2xl rounded-bl-md px-4 py-3" data-id="element-3724" data-mp="2k5e4m6n3f481">
                  <div className="flex gap-1" data-id="element-3725" data-mp="pt7iicsnv30y">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-3726" data-mp="ptzcjj6m0sp4" />
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-3727" data-mp="31h2tmqjfbfet" />
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-3728" data-mp="2rq7ysxs437hr" />
                  </div>
                </div>
              </div>
            </div>}
          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800" data-id="element-3729" data-mp="23vosa7mvdufu">
              {error}
            </div>}
          <div ref={messagesEndRef} data-id="element-3730" data-mp="230mbqiucswd" />
        </div>
        {/* Suggested prompts — only show when conversation is fresh */}
        {messages.length <= 1 && !isLoading && <div className="px-3 md:px-4 pb-2 flex gap-2 overflow-x-auto scrollbar-hide flex-shrink-0 bg-gray-50" data-id="element-3731" data-mp="gu2pblkphjun">
            {suggestedPrompts.map(prompt => <button key={prompt} onClick={() => setInputMessage(prompt)} className="flex-shrink-0 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs text-gray-700 active:bg-gray-100 hover:border-green-500 transition-colors" data-id="element-3732" data-mp="lnfdjulh3omv">
                {prompt}
              </button>)}
          </div>}
        {/* Composer — sticky bottom with safe area */}
        <div className="border-t border-gray-200 bg-white md:rounded-b-2xl flex-shrink-0 pb-[max(0.5rem,env(safe-area-inset-bottom))]" data-id="element-3733" data-mp="pnphz2tma6zm">
          <div className="flex items-end gap-2 p-3 md:p-4" data-id="element-3734" data-mp="uk65buszshbn">
            <textarea ref={inputRef} value={inputMessage} onChange={e => setInputMessage(e.target.value)} onKeyDown={handleKeyPress} placeholder="Ask about medications…" rows={1} className="flex-1 border border-gray-300 rounded-2xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm resize-none max-h-[120px]" disabled={isLoading} dir="auto" data-id="element-3735" data-mp="3acvngniqkcpw" />
            <button onClick={sendMessage} disabled={isLoading || !inputMessage.trim()} className="bg-green-500 hover:bg-green-600 active:bg-green-700 text-white p-2.5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0" aria-label="Send message" data-id="element-3736" data-mp="22era2n13aoda">
              <SendIcon className="w-5 h-5" data-id="element-3737" data-mp="1ni9wrq9cazo3" />
            </button>
          </div>
        </div>
      </div>
    </>;
}
function formatMessage(content: string): string {
  let formatted = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 text-green-600 hover:text-green-700 font-medium underline"><span>$1</span><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>');
  formatted = formatted.replace(/^[•\-]\s+(.+)$/gm, '<div class="flex gap-2 my-1"><span class="text-green-600">•</span><span>$1</span></div>');
  formatted = formatted.replace(/\n/g, '<br>');
  formatted = formatted.replace(/\*\*([^:]+):\*\*/g, '<div class="font-bold text-gray-900 mt-3 mb-1">$1:</div>');
  return formatted;
}